import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  MapPin, 
  Building, 
  Clock, 
  DollarSign, 
  Users, 
  Calendar,
  Star,
  Share2,
  ArrowLeft,
  CheckCircle,
  XCircle,
  ExternalLink
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const JobDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applied, setApplied] = useState(false);
  const [saved, setSaved] = useState(false);

  // Dummy data for demonstration
  useEffect(() => {
    const dummyJob = {
      id: parseInt(id),
      title: 'Frontend Developer',
      company: 'TechCorp Solutions',
      location: 'Mumbai, Maharashtra',
      type: 'Full-time',
      category: 'Frontend Development',
      salary: '₹6-8 LPA',
      experience: '1-3 years',
      description: 'We are looking for a skilled Frontend Developer to join our dynamic team. You will be responsible for building user-friendly web applications and collaborating with our design and backend teams.',
      fullDescription: `
        <h3>About the Role</h3>
        <p>We are seeking a talented Frontend Developer to join our growing team. You will be responsible for creating responsive, user-friendly web applications that provide exceptional user experiences.</p>
        
        <h3>Key Responsibilities</h3>
        <ul>
          <li>Develop and maintain responsive web applications using React.js</li>
          <li>Collaborate with designers to implement pixel-perfect UI designs</li>
          <li>Write clean, maintainable, and well-documented code</li>
          <li>Optimize applications for maximum speed and scalability</li>
          <li>Work closely with backend developers to integrate APIs</li>
          <li>Participate in code reviews and contribute to team best practices</li>
        </ul>
        
        <h3>Requirements</h3>
        <ul>
          <li>1-3 years of experience in frontend development</li>
          <li>Strong proficiency in JavaScript, HTML5, and CSS3</li>
          <li>Experience with React.js and modern JavaScript frameworks</li>
          <li>Knowledge of responsive design principles</li>
          <li>Familiarity with version control systems (Git)</li>
          <li>Good understanding of cross-browser compatibility</li>
        </ul>
        
        <h3>Nice to Have</h3>
        <ul>
          <li>Experience with TypeScript</li>
          <li>Knowledge of state management libraries (Redux, Zustand)</li>
          <li>Familiarity with testing frameworks (Jest, React Testing Library)</li>
          <li>Understanding of web accessibility standards</li>
        </ul>
      `,
      postedDate: '2024-01-15',
      deadline: '2024-02-15',
      skills: ['React', 'JavaScript', 'HTML', 'CSS', 'TypeScript', 'Git'],
      benefits: [
        'Competitive salary package',
        'Health insurance coverage',
        'Flexible working hours',
        'Remote work options',
        'Professional development budget',
        'Annual performance bonuses'
      ],
      requirements: [
        'Bachelor\'s degree in Computer Science or related field',
        '1-3 years of relevant experience',
        'Strong problem-solving skills',
        'Excellent communication abilities',
        'Ability to work in a team environment'
      ],
      logo: 'https://via.placeholder.com/100',
      companyDescription: 'TechCorp Solutions is a leading technology company specializing in innovative software solutions. We work with clients across various industries to deliver cutting-edge applications that drive business growth.',
      contactEmail: 'careers@techcorp.com',
      contactPhone: '+91 98765 43210',
      website: 'https://techcorp.com'
    };

    setJob(dummyJob);
    setLoading(false);
  }, [id]);

  const handleApply = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    // TODO: Implement application logic
    setApplied(true);
    console.log('Applying for job:', id);
  };

  const handleSave = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    setSaved(!saved);
    console.log('Saving job:', id);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: job.title,
        text: `Check out this job opportunity: ${job.title} at ${job.company}`,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      // TODO: Show toast notification
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Job Not Found
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            The job you're looking for doesn't exist or has been removed.
          </p>
          <button
            onClick={() => navigate('/jobs')}
            className="btn-primary"
          >
            Back to Job Listings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate('/jobs')}
          className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
          <span>Back to Job Listings</span>
        </motion.button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Job Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-4">
                  <img
                    src={job.logo}
                    alt={job.company}
                    className="w-16 h-16 rounded-lg object-cover"
                  />
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      {job.title}
                    </h1>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center space-x-1">
                        <Building className="h-4 w-4" />
                        <span>{job.company}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <MapPin className="h-4 w-4" />
                        <span>{job.location}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={handleSave}
                    className={`p-2 rounded-lg transition-colors ${
                      saved 
                        ? 'text-yellow-500 bg-yellow-50 dark:bg-yellow-900/20' 
                        : 'text-gray-400 hover:text-yellow-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                    title="Save Job"
                  >
                    <Star className="h-5 w-5" />
                  </button>
                  <button
                    onClick={handleShare}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    title="Share Job"
                  >
                    <Share2 className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Job Tags */}
              <div className="flex flex-wrap gap-2 mb-6">
                <span className="px-3 py-1 text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full">
                  {job.type}
                </span>
                <span className="px-3 py-1 text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 rounded-full">
                  {job.category}
                </span>
                <span className="px-3 py-1 text-sm font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded-full">
                  {job.experience}
                </span>
              </div>

              {/* Key Details */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="flex items-center space-x-2">
                  <DollarSign className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Salary</p>
                    <p className="font-medium text-gray-900 dark:text-white">{job.salary}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Experience</p>
                    <p className="font-medium text-gray-900 dark:text-white">{job.experience}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Posted</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {new Date(job.postedDate).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="h-5 w-5 text-red-600 dark:text-red-400" />
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Deadline</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {new Date(job.deadline).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  Job Description
                </h3>
                <div 
                  className="prose prose-gray dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: job.fullDescription }}
                />
              </div>
            </motion.div>

            {/* Skills Required */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Skills Required
              </h3>
              <div className="flex flex-wrap gap-2">
                {job.skills.map((skill, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-full"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Benefits */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Benefits & Perks
              </h3>
              <ul className="space-y-2">
                {job.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">{benefit}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Apply Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 sticky top-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Apply for this position
              </h3>
              
              {applied ? (
                <div className="text-center py-4">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-2" />
                  <p className="text-green-600 dark:text-green-400 font-medium">
                    Application Submitted!
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    We'll review your application and get back to you soon.
                  </p>
                </div>
              ) : (
                <button
                  onClick={handleApply}
                  className="w-full btn-primary mb-4"
                  disabled={user?.type !== 'student'}
                >
                  {user?.type === 'student' ? 'Apply Now' : 'Login as Student to Apply'}
                </button>
              )}

              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Applications:</span>
                  <span className="font-medium text-gray-900 dark:text-white">24</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Days left:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {Math.ceil((new Date(job.deadline) - new Date()) / (1000 * 60 * 60 * 24))}
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Company Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                About {job.company}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                {job.companyDescription}
              </p>
              
              <div className="space-y-3">
                <a
                  href={`mailto:${job.contactEmail}`}
                  className="flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:underline"
                >
                  <span>{job.contactEmail}</span>
                </a>
                <a
                  href={`tel:${job.contactPhone}`}
                  className="flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:underline"
                >
                  <span>{job.contactPhone}</span>
                </a>
                <a
                  href={job.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-blue-600 dark:text-blue-400 hover:underline"
                >
                  <span>Visit Website</span>
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </motion.div>

            {/* Similar Jobs */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Similar Jobs
              </h3>
              <div className="space-y-3">
                <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer">
                  <h4 className="font-medium text-gray-900 dark:text-white">React Developer</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">InnovateTech • Mumbai</p>
                  <p className="text-sm text-green-600 dark:text-green-400">₹7-9 LPA</p>
                </div>
                <div className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer">
                  <h4 className="font-medium text-gray-900 dark:text-white">Frontend Engineer</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">WebSolutions • Bangalore</p>
                  <p className="text-sm text-green-600 dark:text-green-400">₹8-12 LPA</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobDetails;
